<template>
  <div class="bg-gray-50 min-h-full" @click="selectedTagForRemoval = null">
    <div class="space-y-4">
      <!-- 搜索和筛选区域 -->
      <div class="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div class="p-4">
          <!-- 第一行：搜索框和主要按钮 -->
          <div class="flex flex-col sm:flex-row gap-4 mb-4">
            <!-- 搜索框 -->
            <div class="flex-1">
              <div class="relative">
                <input
                  v-model="searchQuery"
                  type="text"
                  placeholder="搜索客户姓名、邮箱、电话、订单号等（多个邮箱用逗号分隔：邮箱A,邮箱B）..."
                  class="w-full h-10 pl-10 pr-4 text-sm bg-gray-50/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white transition-all duration-200 placeholder-gray-400"
                  @keyup.enter="handleSearch"
                >
                <MagnifyingGlassIcon class="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
            </div>

            <!-- 筛选器组 -->
            <div class="flex gap-2">
              <!-- 标签筛选 -->
              <div class="flex-1 sm:w-56 sm:flex-initial">
                <SearchableSelect
                  v-model="selectedTag"
                  :options="tagOptions"
                  placeholder="全部标签"
                  label-key="name"
                  value-key="id"
                  @update:modelValue="handleSearch"
                />
              </div>

              <!-- 分组筛选 -->
              <div class="flex-1 sm:w-56 sm:flex-initial">
                <SearchableSelect
                  v-model="selectedGroup"
                  :options="groupOptions"
                  placeholder="全部分组"
                  label-key="name"
                  value-key="id"
                  @update:modelValue="handleSearch"
                />
              </div>
            </div>

            <!-- 日期筛选 -->
            <div class="flex gap-2 items-center">
              <select
                v-model="dateField"
                class="h-10 w-28 px-2 text-sm border border-gray-200 rounded-lg bg-gray-50/50 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              >
                <option value="created_at">创建时间</option>
                <option value="updated_at">更新时间</option>
                <option value="last_contact">最后联系</option>
                <option value="last_email_sent">最后发信</option>
              </select>
              <div class="w-56">
                <DateRangePicker v-model="dateRange" @update:modelValue="handleSearch" />
              </div>
            </div>

            <!-- 搜索和添加客户按钮 -->
            <div class="grid grid-cols-2 gap-2 sm:flex sm:gap-2">
              <button
                @click="handleSearch"
                :disabled="loading"
                class="inline-flex items-center justify-center h-10 px-4 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 hover:border-blue-300 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <MagnifyingGlassIcon class="h-4 w-4 mr-2" />
                搜索客户
              </button>
              <button
                v-if="hasActiveFilters"
                @click="handleResetFilters"
                class="inline-flex items-center justify-center h-10 px-4 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200"
              >
                <XMarkIcon class="h-4 w-4 mr-1" />
                重置
              </button>
              <button
                @click="handleAddCustomer"
                class="inline-flex items-center justify-center h-10 px-4 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg hover:from-blue-700 hover:to-blue-800 focus:ring-2 focus:ring-blue-500/20 focus:ring-offset-2 shadow-sm hover:shadow-md transform hover:scale-105 transition-all duration-200"
              >
                <PlusIcon class="h-4 w-4 mr-2" />
                添加客户
              </button>
            </div>
          </div>

          <!-- 第二行：管理按钮 + 分页 -->
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">

            <!-- 管理和导入导出按钮 -->
            <div class="grid grid-cols-2 gap-2 sm:flex sm:gap-2 sm:flex-wrap">
              <button
                @click="handleManageTags"
                class="inline-flex items-center justify-center h-10 px-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200"
              >
                <TagIcon class="h-4 w-4 mr-2" />
                管理标签
              </button>
              <button
                v-if="canManageGroups"
                @click="handleManageGroups"
                class="inline-flex items-center justify-center h-10 px-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200"
              >
                <FolderIcon class="h-4 w-4 mr-2" />
                管理分组
              </button>
              <button
                v-if="canManageCustomFields"
                @click="handleManageCustomFields"
                class="inline-flex items-center justify-center h-10 px-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200"
              >
                <CogIcon class="h-4 w-4 mr-2" />
                自定义字段
              </button>
              <button
                @click="handleFieldConfig"
                class="inline-flex items-center justify-center h-10 px-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200"
              >
                <AdjustmentsHorizontalIcon class="h-4 w-4 mr-2" />
                字段配置
              </button>
              <button
                v-if="canImportCustomers"
                @click="handleImport"
                class="inline-flex items-center justify-center h-10 px-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200"
              >
                <ArrowUpTrayIcon class="h-4 w-4 mr-2" />
                客户导入
              </button>
              <button
                v-if="canExportCustomers"
                @click="handleExport"
                class="inline-flex items-center justify-center h-10 px-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200"
              >
                <ArrowDownTrayIcon class="h-4 w-4 mr-2" />
                客户导出
              </button>
            </div>
            
            <!-- 顶部分页（与底部样式一致，仅在多页时显示） -->
            <div v-if="!loading && customers.length > 0 && totalPages > 1" class="hidden sm:flex items-center space-x-2">
              <button
                @click="prevPage"
                :disabled="currentPage === 1 || loading"
                class="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeftIcon class="h-4 w-4" />
                上一页
              </button>
              
              <span class="px-3 py-2 text-sm font-medium text-gray-700">
                第 {{ currentPage }} / {{ totalPages }} 页
              </span>
              
              <button
                @click="nextPage"
                :disabled="currentPage === totalPages || loading"
                class="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                下一页
                <ChevronRightIcon class="h-4 w-4" />
              </button>
              
              <span class="text-sm text-gray-500 ml-2">跳转至</span>
              <input
                v-model="topJumpToPage"
                type="number"
                :min="1"
                :max="totalPages"
                placeholder="页码"
                class="w-16 h-8 px-2 text-sm text-center border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                @keyup.enter="handleTopJumpToPage"
              />
              <button
                @click="handleTopJumpToPage"
                :disabled="loading"
                class="h-8 px-3 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                确定
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 客户列表 -->
      <div class="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div v-if="loading" class="flex items-center justify-center py-20">
          <div class="text-center">
            <svg class="animate-spin h-8 w-8 text-blue-600 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p class="text-gray-500">加载中...</p>
          </div>
        </div>

        <!-- 空状态 -->
        <div v-else-if="customers.length === 0" class="text-center py-20">
          <UsersIcon class="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 class="text-lg font-medium text-gray-900 mb-2">
            {{ apiError ? '加载失败' : (searchQuery || selectedTag || selectedGroup ? '无匹配结果' : '暂无客户') }}
          </h3>
          <p class="text-gray-500 mb-6">
            {{ apiError ? '请检查网络连接或稍后重试' : (searchQuery || selectedTag || selectedGroup ? '尝试调整搜索条件或筛选条件' : '开始添加您的第一个客户') }}
          </p>
          <button
            v-if="!apiError"
            @click="handleAddCustomer"
            class="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500/20 focus:ring-offset-2 transition-all duration-200"
          >
            <PlusIcon class="h-4 w-4 mr-2" />
            添加客户
          </button>
        </div>

        <!-- 批量操作栏 -->
        <div v-if="selectedCustomers.length > 0" class="bg-blue-50 px-4 sm:px-6 py-3 border-b border-gray-200">
          <!-- 桌面端：横向布局 -->
          <div class="hidden sm:flex items-center justify-between">
            <div class="flex items-center space-x-3">
              <span class="text-sm text-gray-700">已选择 {{ selectedCustomers.length }} 个客户</span>
              <button
                type="button"
                @click="selectedCustomers = []"
                class="text-sm text-gray-500 hover:text-gray-700"
              >
                取消选择
              </button>
            </div>
            <div class="flex items-center space-x-2">
              <button
                type="button"
                @click="showBulkTagModal = true"
                class="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                添加标签
              </button>
              <button
                type="button"
                @click="showBulkRemoveTagModal = true"
                class="inline-flex items-center px-3 py-1 border border-orange-300 shadow-sm text-sm font-medium rounded-md text-orange-700 bg-white hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
              >
                移除标签
              </button>
              <button
                type="button"
                @click="showBulkGroupModal = true"
                class="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                修改分组
              </button>
              <button
                type="button"
                @click="handleBulkSendEmail"
                class="inline-flex items-center px-3 py-1 border border-green-300 shadow-sm text-sm font-medium rounded-md text-green-700 bg-white hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                群发邮件
              </button>
              <button
                v-if="canDeleteCustomers"
                type="button"
                @click="handleBulkDelete"
                :disabled="bulkDeleting"
                class="inline-flex items-center px-3 py-1 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg v-if="bulkDeleting" class="animate-spin -ml-1 mr-2 h-4 w-4 text-red-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ bulkDeleting ? '删除中...' : '批量删除' }}
              </button>
            </div>
          </div>

          <!-- 手机端：纵向布局 -->
          <div class="block sm:hidden space-y-3">
            <!-- 选择信息行 -->
            <div class="flex items-center justify-between">
              <span class="text-sm text-gray-700">已选择 {{ selectedCustomers.length }} 个客户</span>
              <button
                type="button"
                @click="selectedCustomers = []"
                class="text-sm text-gray-500 hover:text-gray-700"
              >
                取消选择
              </button>
            </div>
            
            <!-- 操作按钮列表 -->
            <div class="grid grid-cols-5 gap-1">
              <button
                type="button"
                @click="showBulkTagModal = true"
                class="inline-flex items-center justify-center px-1 py-2 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-blue-500"
              >
                添加标签
              </button>
              <button
                type="button"
                @click="showBulkRemoveTagModal = true"
                class="inline-flex items-center justify-center px-1 py-2 border border-orange-300 shadow-sm text-xs font-medium rounded-md text-orange-700 bg-white hover:bg-orange-50 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-orange-500"
              >
                移除标签
              </button>
              <button
                type="button"
                @click="showBulkGroupModal = true"
                class="inline-flex items-center justify-center px-1 py-2 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-blue-500"
              >
                修改分组
              </button>
              <button
                type="button"
                @click="handleBulkSendEmail"
                class="inline-flex items-center justify-center px-1 py-2 border border-green-300 shadow-sm text-xs font-medium rounded-md text-green-700 bg-white hover:bg-green-50 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-green-500"
              >
                群发邮件
              </button>
              <button
                v-if="canDeleteCustomers"
                type="button"
                @click="handleBulkDelete"
                :disabled="bulkDeleting"
                class="inline-flex items-center justify-center px-1 py-2 border border-red-300 shadow-sm text-xs font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg v-if="bulkDeleting" class="animate-spin h-3 w-3 text-red-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span v-if="!bulkDeleting">删除</span>
                <span v-else class="ml-1">删除中</span>
              </button>
            </div>
          </div>
        </div>

        <!-- 客户数据内容 -->
        <div v-if="!loading && customers.length > 0">
          <!-- 桌面端表格视图 -->
          <div class="hidden sm:block relative">
            <div class="flex overflow-hidden" ref="tableContainer">
              <!-- 左侧固定列：复选框 -->
              <div class="flex-shrink-0 bg-white" ref="leftTableContainer">
                <table class="w-[60px]">
            <thead class="bg-gray-50">
              <tr>
                      <th class="pl-6 pr-2 py-3 text-left w-[60px] h-[52px]">
                  <input
                    type="checkbox"
                    :checked="isAllSelected"
                    :indeterminate="isIndeterminate"
                    @change="toggleSelectAll"
                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
                    <tr v-for="customer in customers" :key="`checkbox-${customer.id}`" class="hover:bg-gray-50 group">
                      <td class="pl-6 pr-2 py-4 whitespace-nowrap w-[60px] group-hover:bg-gray-50 transition-colors">
                  <input
                    type="checkbox"
                    :value="customer.id"
                    v-model="selectedCustomers"
                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- 中间滚动区域：数据列 -->
              <div class="flex-1 overflow-x-auto table-container" ref="middleTableContainer">
                <table class="w-full table-auto min-w-max">
                  <thead class="bg-gray-50">
                    <tr>
                      <th 
                        v-for="fieldConfig in visibleFieldConfigs" 
                        :key="`header-${fieldConfig.field_name}`"
                        class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider h-[52px]"
                        :style="{ width: fieldConfig.column_width ? `${fieldConfig.column_width}px` : 'auto' }"
                      >
                        {{ fieldConfig.field_label }}
                      </th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    <tr v-for="customer in customers" :key="`data-${customer.id}`" class="hover:bg-gray-50 group">
                <td 
                  v-for="fieldConfig in visibleFieldConfigs" 
                  :key="`data-${customer.id}-${fieldConfig.field_name}`"
                  class="px-6 py-4 text-sm text-gray-500"
                  :style="{ width: fieldConfig.column_width ? `${fieldConfig.column_width}px` : 'auto' }"
                >
                  <!-- 特殊字段渲染 -->
                  <template v-if="renderFieldValue(customer, fieldConfig)?.type === 'address'">
                    <div 
                      class="max-w-[160px] overflow-hidden"
                      :title="renderFieldValue(customer, fieldConfig).value"
                      style="display: -webkit-box; -webkit-line-clamp: 2; line-clamp: 2; -webkit-box-orient: vertical;"
                    >
                      {{ renderFieldValue(customer, fieldConfig).value }}
                    </div>
                  </template>
                  
                  <template v-else-if="renderFieldValue(customer, fieldConfig)?.type === 'tags'">
                    <div class="flex flex-wrap gap-1 max-w-[180px]">
                      <span
                        v-for="tag in renderFieldValue(customer, fieldConfig).value"
                        :key="tag.id"
                        @click="handleTagClick(customer.id, tag.id, $event)"
                        :style="{ 
                          backgroundColor: tag.background_color || '#3B82F6', 
                          color: tag.text_color || '#FFFFFF' 
                        }"
                        :class="[
                          'relative inline-flex items-center px-2 py-1 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer',
                          selectedTagForRemoval === `${customer.id}-${tag.id}` ? 'ring-2 ring-red-400 ring-opacity-75' : 'hover:brightness-110'
                        ]"
                      >
                        {{ tag.name }}
                        <button
                          v-if="selectedTagForRemoval === `${customer.id}-${tag.id}`"
                          @click.stop="handleRemoveTagFromCustomer(customer, tag)"
                          :disabled="customer.removingTag === tag.id"
                          class="absolute -top-1 -right-1 h-4 w-4 flex items-center justify-center rounded-full bg-red-500 hover:bg-red-600 text-white transition-all duration-200 disabled:opacity-50 shadow-sm"
                          :title="`从${customer.first_name} ${customer.last_name}移除标签 ${tag.name}`"
                        >
                          <svg v-if="customer.removingTag === tag.id" class="animate-spin h-2 w-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <svg v-else class="h-2 w-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                          </svg>
                        </button>
                      </span>
                      <span v-if="!renderFieldValue(customer, fieldConfig).value || renderFieldValue(customer, fieldConfig).value.length === 0" class="text-xs text-gray-400">
                        无标签
                      </span>
                    </div>
                  </template>
                  
                  <template v-else-if="renderFieldValue(customer, fieldConfig)?.type === 'groups'">
                    <div v-if="renderFieldValue(customer, fieldConfig).value && renderFieldValue(customer, fieldConfig).value.length > 0" class="flex flex-wrap gap-1">
                      <span
                        v-for="group in renderFieldValue(customer, fieldConfig).value"
                        :key="group.id"
                        :style="{ backgroundColor: (group.color || '#3B82F6') + '20', color: group.color || '#3B82F6' }"
                        class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                      >
                        {{ group.name }}
                      </span>
                    </div>
                    <span v-else class="text-gray-400">-</span>
                  </template>
                  
                  <template v-else-if="renderFieldValue(customer, fieldConfig)?.type === 'remark'">
                    <div class="max-w-[140px] truncate" :title="renderFieldValue(customer, fieldConfig).value">
                      {{ renderFieldValue(customer, fieldConfig).value }}
                    </div>
                  </template>
                  
                  
                        <!-- 普通字段显示 -->
                        <template v-else-if="!renderFieldValue(customer, fieldConfig)?.type">
                          <span class="text-gray-700">{{ renderFieldValue(customer, fieldConfig) }}</span>
                        </template>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- 右侧固定列：操作 -->
              <div 
                v-if="actionFieldConfig && actionFieldConfig.is_visible"
                class="flex-shrink-0 bg-gray-50"
                :style="{ width: actionFieldConfig.column_width ? `${actionFieldConfig.column_width}px` : '180px' }"
                ref="rightTableContainer"
              >
                <table class="w-full">
                  <thead class="bg-gray-100">
                    <tr>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider h-[52px]">
                        {{ actionFieldConfig.field_label }}
                      </th>
                    </tr>
                  </thead>
                  <tbody class="bg-gray-50 divide-y divide-gray-200">
                    <tr v-for="customer in customers" :key="`action-${customer.id}`" class="hover:bg-gray-100 group">
                      <td class="px-6 py-4 text-sm text-gray-500 group-hover:bg-gray-100 transition-colors">
                    <div class="flex items-center space-x-3">
                      <!-- 发信 -->
                      <button
                        @click="handleSendEmail(customer)"
                        class="inline-flex items-center text-blue-600 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded text-xs transition-colors"
                        title="发送邮件"
                      >
                        <EnvelopeIcon class="h-4 w-4 mr-1" />
                        发信
                      </button>

                      <!-- 编辑 -->
                      <button
                        @click="handleEditCustomer(customer)"
                            class="inline-flex items-center text-gray-600 hover:text-gray-900 bg-white hover:bg-gray-50 px-2 py-1 rounded text-xs transition-colors"
                        title="编辑客户"
                      >
                        <PencilIcon class="h-4 w-4 mr-1" />
                        编辑
                      </button>

                      <!-- 删除 -->
                      <button
                        v-if="canDeleteCustomers"
                        @click="handleDeleteCustomer(customer)"
                        :disabled="customer.deleting"
                        class="inline-flex items-center text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 px-2 py-1 rounded text-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="删除客户"
                      >
                        <TrashIcon v-if="!customer.deleting" class="h-4 w-4 mr-1" />
                        <svg v-else class="animate-spin h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 818-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {{ customer.deleting ? '删除中...' : '删除' }}
                      </button>
                    </div>
                </td>
              </tr>
            </tbody>
          </table>
              </div>
            </div>
          </div>

          <!-- 手机端卡片视图 -->
          <div class="block sm:hidden space-y-4">
            <!-- 全选功能 -->
            <div class="flex items-center justify-between bg-gray-50 px-4 py-3 rounded-lg">
              <div class="flex items-center space-x-3">
                <input
                  type="checkbox"
                  :checked="isAllSelected"
                  :indeterminate="isIndeterminate"
                  @change="toggleSelectAll"
                  class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span class="text-sm text-gray-700">全选</span>
              </div>
              <span class="text-sm text-gray-500">
                {{ selectedCustomers.length > 0 ? `已选择 ${selectedCustomers.length} 个` : `共 ${customers.length} 个客户` }}
              </span>
            </div>

            <!-- 客户卡片列表 -->
            <div
              v-for="customer in customers"
              :key="customer.id"
              class="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow"
            >
              <!-- 卡片头部 -->
              <div class="p-4 border-b border-gray-100">
                <div class="flex items-start justify-between">
                  <div class="flex items-start space-x-3 flex-1">
                    <!-- 选择复选框 -->
                    <input
                      type="checkbox"
                      :value="customer.id"
                      v-model="selectedCustomers"
                      class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
                    />
                    <!-- 客户基本信息 -->
                    <div class="flex-1 min-w-0">
                      <h3 class="text-base font-medium text-gray-900 truncate">
                        {{ customer.first_name }} {{ customer.last_name }}
                      </h3>
                      <p class="text-sm text-gray-500 truncate">{{ customer.email }}</p>
                      <div class="flex flex-wrap gap-2 mt-2">
                        <span v-if="customer.phone" class="inline-flex items-center text-xs text-gray-600">
                          📞 {{ customer.phone }}
                        </span>
                        <span v-if="customer.source" class="inline-flex items-center text-xs text-gray-600">
                          🏷️ {{ customer.source }}
                        </span>
                      </div>
                    </div>
                  </div>
                  <!-- 时间 -->
                  <div class="text-xs text-gray-400 ml-2">
                    {{ formatDate(customer.created_at).split(' ')[0] }}
                  </div>
                </div>
              </div>

              <!-- 卡片内容 -->
              <div class="p-4 space-y-3">
                <!-- 订单信息 -->
                <div v-if="customer.order_number" class="flex items-center space-x-2">
                  <span class="text-xs font-medium text-gray-500">订单号:</span>
                  <span class="text-sm text-gray-900">{{ customer.order_number }}</span>
                </div>

                <!-- 地址 -->
                <div v-if="customer.address" class="flex items-start space-x-2">
                  <span class="text-xs font-medium text-gray-500 mt-0.5">地址:</span>
                  <span class="text-sm text-gray-900 flex-1 leading-relaxed">{{ customer.address }}</span>
                </div>

                <!-- 备注 -->
                <div v-if="customer.notes" class="flex items-start space-x-2">
                  <span class="text-xs font-medium text-gray-500 mt-0.5">备注:</span>
                  <span class="text-sm text-gray-900 flex-1 leading-relaxed">{{ customer.notes }}</span>
                </div>

                <!-- 标签 -->
                <div v-if="customer.tags && customer.tags.length > 0">
                  <div class="flex items-center space-x-2 mb-2">
                    <span class="text-xs font-medium text-gray-500">标签:</span>
                  </div>
                  <div class="flex flex-wrap gap-1">
                    <span
                      v-for="tag in customer.tags"
                      :key="tag.id"
                      @click="handleTagClick(customer.id, tag.id, $event)"
                      :style="{ 
                        backgroundColor: tag.background_color || '#3B82F6', 
                        color: tag.text_color || '#FFFFFF' 
                      }"
                      :class="[
                        'relative inline-flex items-center px-2 py-1 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer',
                        selectedTagForRemoval === `${customer.id}-${tag.id}` ? 'ring-2 ring-red-400 ring-opacity-75' : 'hover:brightness-110'
                      ]"
                    >
                      {{ tag.name }}
                      <button
                        v-if="selectedTagForRemoval === `${customer.id}-${tag.id}`"
                        @click.stop="handleRemoveTagFromCustomer(customer, tag)"
                        :disabled="customer.removingTag === tag.id"
                        class="absolute -top-1 -right-1 h-4 w-4 flex items-center justify-center rounded-full bg-red-500 hover:bg-red-600 text-white transition-all duration-200 disabled:opacity-50 shadow-sm"
                        :title="`从${customer.first_name} ${customer.last_name}移除标签 ${tag.name}`"
                      >
                        <svg v-if="customer.removingTag === tag.id" class="animate-spin h-2 w-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <svg v-else class="h-2 w-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                        </svg>
                      </button>
                    </span>
                  </div>
                </div>

                <!-- 分组 -->
                <div v-if="customer.groups && customer.groups.length > 0">
                  <div class="flex items-center space-x-2 mb-2">
                    <span class="text-xs font-medium text-gray-500">分组:</span>
                  </div>
                  <div class="flex flex-wrap gap-1">
                    <span
                      v-for="group in customer.groups"
                      :key="group.id"
                      :style="{ backgroundColor: (group.color || '#3B82F6') + '20', color: group.color || '#3B82F6' }"
                      class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                    >
                      {{ group.name }}
                    </span>
                  </div>
                </div>

                <!-- 自定义字段 -->
                <div v-if="customer.custom_fields && customer.custom_fields.length > 0">
                  <div class="flex items-center space-x-2 mb-2">
                    <span class="text-xs font-medium text-gray-500">自定义字段:</span>
                  </div>
                  <div class="space-y-2">
                    <div
                      v-for="field in customer.custom_fields"
                      :key="field.field_id"
                      class="flex items-start space-x-2"
                    >
                      <span class="text-xs font-medium text-gray-500 mt-0.5">{{ field.label }}:</span>
                      <span class="text-sm text-gray-900 flex-1 leading-relaxed">{{ field.value || '-' }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 卡片底部操作 -->
              <div class="px-4 py-3 bg-gray-50 border-t border-gray-100 rounded-b-lg">
                <div class="flex items-center justify-between space-x-2">
                  <div class="flex items-center space-x-2">
                    <!-- 发信 -->
                    <button
                      @click="handleSendEmail(customer)"
                      class="inline-flex items-center text-blue-600 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 px-3 py-2 rounded-md text-xs font-medium transition-colors border border-blue-200"
                      title="发送邮件"
                    >
                      <EnvelopeIcon class="h-4 w-4 mr-1" />
                      发信
                    </button>

                    <!-- 编辑 -->
                    <button
                      @click="handleEditCustomer(customer)"
                      class="inline-flex items-center text-gray-600 hover:text-gray-900 bg-white hover:bg-gray-50 px-3 py-2 rounded-md text-xs font-medium transition-colors border border-gray-200"
                      title="编辑客户"
                    >
                      <PencilIcon class="h-4 w-4 mr-1" />
                      编辑
                    </button>
                  </div>

                  <!-- 删除 -->
                  <button
                    v-if="canDeleteCustomers"
                    @click="handleDeleteCustomer(customer)"
                    :disabled="customer.deleting"
                    class="inline-flex items-center text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 px-3 py-2 rounded-md text-xs font-medium transition-colors border border-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="删除客户"
                  >
                    <TrashIcon v-if="!customer.deleting" class="h-4 w-4 mr-1" />
                    <svg v-else class="animate-spin h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {{ customer.deleting ? '删除中...' : '删除' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 分页 -->
        <div v-if="customers.length > 0" class="bg-white border-t border-gray-200">
          <!-- 移动端分页：简化版本 -->
          <div class="px-4 py-3 sm:hidden space-y-3">
            <!-- 第一行：分页按钮和页面信息 -->
            <div class="flex justify-between items-center">
              <button
                @click="prevPage"
                :disabled="currentPage === 1 || loading"
                class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                上一页
              </button>
              
              <span class="text-sm font-medium text-gray-700">
                第 {{ currentPage }} / {{ totalPages }} 页
              </span>
              
              <button
                @click="nextPage"
                :disabled="currentPage === totalPages || loading"
                class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                下一页
              </button>
            </div>
            
            <!-- 第二行：每页数量和页码跳转 -->
            <div class="flex justify-between items-center">
              <!-- 每页数量选择器 -->
              <div class="flex items-center space-x-2">
                <span class="text-sm text-gray-500">每页</span>
                <select
                  v-model="pageSize"
                  @change="handlePageSizeChange"
                  class="h-8 border border-gray-300 rounded-md px-2 pr-6 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <span class="text-sm text-gray-500">条</span>
              </div>
              
              <!-- 页码跳转 -->
              <div class="flex items-center space-x-3">
                <span class="text-sm text-gray-500">跳转至</span>
                <input
                  v-model="jumpToPage"
                  type="number"
                  :min="1"
                  :max="totalPages"
                  placeholder="页码"
                  class="w-16 h-8 px-2 text-sm text-center border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                  @keyup.enter="handleJumpToPage"
                />
                <button
                  @click="handleJumpToPage"
                  :disabled="loading"
                  class="h-8 px-3 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  确定
                </button>
              </div>
            </div>
          </div>
          
          <!-- 桌面端分页：显示完整信息 -->
          <div class="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between px-6 py-3">
            <div class="flex items-center space-x-4">
              <span class="text-sm text-gray-700">
                显示第 {{ (currentPage - 1) * pageSize + 1 }} - {{ Math.min(currentPage * pageSize, totalCustomers) }} 条，
                共 {{ totalCustomers }} 条记录
              </span>
              
              <!-- 每页数量选择器 -->
              <div class="flex items-center space-x-2">
                <span class="text-sm text-gray-700">每页</span>
                <select
                  v-model="pageSize"
                  @change="handlePageSizeChange"
                  class="border border-gray-300 rounded-md px-3 py-1 pr-8 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[60px] w-auto"
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <span class="text-sm text-gray-700">条</span>
              </div>
            </div>
            <div class="flex items-center justify-between">
              <!-- 左侧：上一页下一页 -->
              <div class="flex items-center space-x-2">
                <button
                  @click="prevPage"
                  :disabled="currentPage === 1 || loading"
                  class="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon class="h-4 w-4" />
                  上一页
                </button>
                
                <span class="px-3 py-2 text-sm font-medium text-gray-700">
                  第 {{ currentPage }} / {{ totalPages }} 页
                </span>
                
                <button
                  @click="nextPage"
                  :disabled="currentPage === totalPages || loading"
                  class="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  下一页
                  <ChevronRightIcon class="h-4 w-4" />
                </button>
              </div>
              
              <!-- 右侧：页码跳转 -->
              <div class="flex items-center space-x-3 ml-6">
                <span class="text-sm text-gray-500">跳转至</span>
                <input
                  v-model="jumpToPage"
                  type="number"
                  :min="1"
                  :max="totalPages"
                  placeholder="页码"
                  class="w-16 h-8 px-2 text-sm text-center border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                  @keyup.enter="handleJumpToPage"
                />
                <button
                  @click="handleJumpToPage"
                  :disabled="loading"
                  class="h-8 px-3 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  确定
                </button>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>

    <!-- 标签管理模态框 -->
    <TagManagementModalV2 
      :show="showTagManagementModal"
      @close="closeTagManagementModal"
      @success="handleTagManagementSuccess"
    />

    <!-- 分组管理模态框 -->
    <GroupManagementModal 
      :show="showGroupManagementModal"
      @close="closeGroupManagementModal"
      @success="handleGroupManagementSuccess"
    />

    <!-- 自定义字段管理模态框 -->
    <CustomerCustomFieldManagementModal 
      :show="showCustomFieldManagementModal"
      @close="closeCustomFieldManagementModal"
      @success="handleCustomFieldManagementSuccess"
    />

    <!-- 字段配置模态框 -->
    <CustomerFieldConfigModal 
      :show="showFieldConfigModal"
      @close="closeFieldConfigModal"
      @success="handleFieldConfigSuccess"
    />

    <!-- 导出模态框 -->
    <ExportModal 
      :show="showExportModal"
      :total-count="allCustomersCount"
      :filtered-count="totalCustomers"
      :selected-customers="selectedCustomers"
      :has-filters="hasActiveFilters"
      @close="closeExportModal"
      @export="handleConfirmExport"
    />

    <!-- 导入模态框 -->
    <ImportModal 
      v-if="showImportModal"
      :show="showImportModal"
      :tags="availableTags"
      :groups="availableGroups"
      @close="closeImportModal"
      @imported="handleImportCompleted"
    />

    <!-- 批量添加标签模态框 -->
    <BulkTagModal 
      :show="showBulkTagModal"
      :tags="availableTags"
      :selected-count="selectedCustomers.length"
      @close="showBulkTagModal = false"
      @confirm="handleBulkAddTags"
    />

    <!-- 批量移除标签模态框 -->
    <BulkRemoveTagModal 
      :show="showBulkRemoveTagModal"
      :tags="availableTags"
      :selected-count="selectedCustomers.length"
      @close="showBulkRemoveTagModal = false"
      @confirm="handleBulkRemoveTags"
    />

    <!-- 批量修改分组模态框 -->
    <BulkGroupModal 
      :show="showBulkGroupModal"
      :groups="availableGroups"
      :selected-count="selectedCustomers.length"
      @close="showBulkGroupModal = false"
      @confirm="handleBulkChangeGroup"
    />

    <!-- 客户编辑/添加模态框 -->
    <CustomerModal 
      :show="showCustomerModal"
      :customer="editingCustomer"
      :available-tags="availableTags"
      :available-groups="availableGroups"
      :custom-fields-update-trigger="customFieldsUpdateTrigger"
      @close="closeCustomerModal"
      @success="handleCustomerModalSuccess"
    />

    <!-- 邮件发送模态框 -->
    <EmailReplyModal
      :show-modal="showEmailModal"
      :original-email="null"
      :is-reply="false"
      :target-customer="targetCustomer"
      :bulk-recipients="bulkEmailRecipients"
      @close="closeEmailModal"
      @sent="onEmailSent"
    />
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import { useAuthStore } from '@/stores/auth'
import { customerService, customerTagService, customerTagGroupService, customerGroupService, customerFieldConfigService } from '@/services/customerService'
  import TagManagementModalV2 from '@/components/customers/TagManagementModalV2.vue'
  import GroupManagementModal from '@/components/customers/GroupManagementModal.vue'
  import CustomerCustomFieldManagementModal from '@/components/customers/CustomerCustomFieldManagementModal.vue'
  import CustomerFieldConfigModal from '@/components/customers/CustomerFieldConfigModal.vue'
  import ExportModal from '@/components/customers/ExportModal.vue'
  import ImportModal from '@/components/customers/ImportModal.vue'
  import BulkTagModal from '@/components/customers/BulkTagModal.vue'
  import BulkRemoveTagModal from '@/components/customers/BulkRemoveTagModal.vue'
  import BulkGroupModal from '@/components/customers/BulkGroupModal.vue'
  import CustomerModal from '@/components/customers/CustomerModal.vue'
  import EmailReplyModal from '@/components/EmailReplyModal.vue'
  import SearchableSelect from '@/components/common/SearchableSelect.vue'
  import DateRangePicker from '@/components/common/DateRangePicker.vue'
import { 
  MagnifyingGlassIcon, 
  PlusIcon, 
  UsersIcon,
  EnvelopeIcon,
  PencilIcon, 
  TrashIcon, 
  TagIcon,
  FolderIcon,
  CogIcon,
  AdjustmentsHorizontalIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon
} from '@heroicons/vue/24/outline'

const router = useRouter()
const toast = useToast()
const authStore = useAuthStore()

// 响应式数据
const searchQuery = ref('')
const selectedTag = ref('')
const selectedGroup = ref('')
const dateField = ref('created_at')
const dateRange = ref({ start: '', end: '' })
const currentPage = ref(1)
const pageSize = ref(10)
const jumpToPage = ref('')
const topJumpToPage = ref('')
const customers = ref([])
const loading = ref(true)
const totalCustomers = ref(0) // 当前筛选结果的总数
const allCustomersCount = ref(0) // 全部客户的总数（不受筛选影响）
const apiError = ref('')
const fieldConfigs = ref([]) // 字段配置

// 批量操作
const selectedCustomers = ref([])
const bulkDeleting = ref(false)

// 选中要删除的标签状态
const selectedTagForRemoval = ref(null)

// 表格容器引用
const tableContainer = ref(null)
const leftTableContainer = ref(null)
const middleTableContainer = ref(null)
const rightTableContainer = ref(null)

// 模态框状态
const showCustomerModal = ref(false)
const editingCustomer = ref(null)
  const showTagManagementModal = ref(false)
  const showGroupManagementModal = ref(false)
  const showCustomFieldManagementModal = ref(false)
  const showFieldConfigModal = ref(false)
  const showExportModal = ref(false)
  const showImportModal = ref(false)

// 自定义字段更新触发器
const customFieldsUpdateTrigger = ref(0)
const showBulkTagModal = ref(false)
const showBulkRemoveTagModal = ref(false)
const showBulkGroupModal = ref(false)

// 邮件发送模态框状态
const showEmailModal = ref(false)
const targetCustomer = ref(null)
const bulkEmailRecipients = ref('')

// 标签和分组数据
const availableTags = ref([])
const availableGroups = ref([])

// 权限检查
const isSubAccount = computed(() => {
  return authStore.user?.account_type === 'sub'
})

// 🔧 检查子账号的删除权限
const canDeleteCustomers = computed(() => {
  // 主账号总是可以删除
  if (!isSubAccount.value) {
    return true
  }
  // 子账号检查 sub_permissions
  return authStore.user?.sub_permissions?.can_delete_customers !== false
})

// 🔧 检查子账号的导入导出权限
const canImportCustomers = computed(() => {
  // 主账号总是可以导入
  if (!isSubAccount.value) {
    return true
  }
  // 子账号检查 sub_permissions
  return authStore.user?.sub_permissions?.can_import_customers !== false
})

const canExportCustomers = computed(() => {
  // 主账号总是可以导出
  if (!isSubAccount.value) {
    return true
  }
  // 子账号检查 sub_permissions
  return authStore.user?.sub_permissions?.can_export_customers === true
})

// 🔧 子账号不能管理（创建/编辑/删除）自定义字段
const canManageCustomFields = computed(() => {
  return !isSubAccount.value
})

const canManageGroups = computed(() => {
  // 子账户也可以打开分组管理（仅编辑，不能创建/删除，具体由模态框内部控制）
  return true
})

// 计算属性
const totalPages = computed(() => Math.ceil(totalCustomers.value / pageSize.value))

// 批量操作计算属性
const isAllSelected = computed(() => {
  return customers.value.length > 0 && selectedCustomers.value.length === customers.value.length
})

const isIndeterminate = computed(() => {
  return selectedCustomers.value.length > 0 && selectedCustomers.value.length < customers.value.length
})

// 导出相关计算属性
const hasActiveFilters = computed(() => {
  return searchQuery.value.trim() !== '' ||
         selectedTag.value ||
         selectedGroup.value ||
         dateRange.value.start
})

// 标签选项格式化
const tagOptions = computed(() => {
  return [
    { id: '', name: '全部标签' },
    ...availableTags.value.map(tag => ({ id: tag.id, name: tag.name }))
  ]
})

// 分组选项格式化
const groupOptions = computed(() => {
  return [
    { id: '', name: '全部分组' },
    ...availableGroups.value.map(group => ({ id: group.id, name: group.name }))
  ]
})

// 可见字段配置（按排序顺序，排除操作列）
const visibleFieldConfigs = computed(() => {
  return fieldConfigs.value
    .filter(config => config.is_visible && config.field_name !== 'actions')
    .sort((a, b) => a.sort_order - b.sort_order)
})

// 操作列配置
const actionFieldConfig = computed(() => {
  return fieldConfigs.value.find(config => config.field_name === 'actions')
})

// 批量操作方法
const toggleSelectAll = () => {
  if (isAllSelected.value) {
    selectedCustomers.value = []
  } else {
    selectedCustomers.value = customers.value.map(customer => customer.id)
  }
}

const handleBulkDelete = async () => {
  if (selectedCustomers.value.length === 0) {
    toast.warning('请选择要删除的客户')
    return
  }

  const count = selectedCustomers.value.length
  if (confirm(`⚠️ 确定要永久删除选中的 ${count} 个客户吗？\n\n删除后将从数据库中彻底移除，无法恢复！\n\n所有相关的标签、分组关联关系也将被清除。`)) {
    bulkDeleting.value = true
    
    try {
      // 使用批量操作API一次性删除所有选中的客户
      const result = await customerService.bulkAction({
        customer_ids: selectedCustomers.value,
        action: 'delete'
      })
      
      if (result.success) {
        toast.success(result.message || `成功删除 ${count} 个客户`)
        
        // 清空选择
        selectedCustomers.value = []
        
        // 刷新数据以确保前后端一致性
        await searchCustomers(false)
        await loadAllCustomersCount()
        
        // 如果当前页没有数据了，且不是第一页，则跳转到上一页
        if (customers.value.length === 0 && currentPage.value > 1) {
          currentPage.value--
          await searchCustomers(false)
        }
      } else {
        toast.error(result.message || '批量删除失败')
      }
      
    } catch (error) {
      console.error('批量删除失败:', error)
      toast.error('批量删除失败，请稍后重试')
    } finally {
      bulkDeleting.value = false
    }
  }
}

// 同步表格行高度
const syncTableRowHeights = async () => {
  await nextTick()
  
  if (!leftTableContainer.value || !middleTableContainer.value || !rightTableContainer.value) {
    return
  }
  
  try {
    const leftRows = leftTableContainer.value.querySelectorAll('tbody tr')
    const middleRows = middleTableContainer.value.querySelectorAll('tbody tr')
    const rightRows = rightTableContainer.value.querySelectorAll('tbody tr')
    
    if (leftRows.length !== middleRows.length || middleRows.length !== rightRows.length) {
      return
    }
    
    // 重置所有行的高度为auto
    leftRows.forEach(row => row.style.height = 'auto')
    middleRows.forEach(row => row.style.height = 'auto')
    rightRows.forEach(row => row.style.height = 'auto')
    
    // 等待一帧以确保高度计算正确
    await new Promise(resolve => requestAnimationFrame(resolve))
    
    // 计算每一行的最大高度并应用
    for (let i = 0; i < middleRows.length; i++) {
      const leftHeight = leftRows[i]?.offsetHeight || 0
      const middleHeight = middleRows[i]?.offsetHeight || 0
      const rightHeight = rightRows[i]?.offsetHeight || 0
      
      const maxHeight = Math.max(leftHeight, middleHeight, rightHeight)
      
      if (maxHeight > 0) {
        leftRows[i].style.height = `${maxHeight}px`
        middleRows[i].style.height = `${maxHeight}px`
        rightRows[i].style.height = `${maxHeight}px`
      }
    }
  } catch (error) {
    console.warn('同步表格行高度时出错:', error)
  }
}

const handleBulkAddTags = async (selectedTagIds) => {
  if (selectedCustomers.value.length === 0) {
    toast.warning('请选择要添加标签的客户')
    return
  }

  if (selectedTagIds.length === 0) {
    toast.warning('请选择要添加的标签')
    return
  }

  try {
    const result = await customerService.bulkAction({
      customer_ids: selectedCustomers.value,
      action: 'add_tags',
      tag_ids: selectedTagIds
    })

    if (result.success) {
      toast.success(result.message || `成功为 ${selectedCustomers.value.length} 个客户添加标签`)
      // 清空选择
      selectedCustomers.value = []
      // 重新加载数据以更新显示
      await searchCustomers(false)
    } else {
      toast.error(result.message || '添加标签失败')
      // API调用失败时也清空选择
      selectedCustomers.value = []
    }
  } catch (error) {
    console.error('批量添加标签失败:', error)
    toast.error('添加标签失败，请稍后重试')
    // 出错时也要清空选择
    selectedCustomers.value = []
  }
}

const handleBulkRemoveTags = async (selectedTagIds) => {
  if (selectedCustomers.value.length === 0) {
    toast.warning('请选择要移除标签的客户')
    return
  }

  if (selectedTagIds.length === 0) {
    toast.warning('请选择要移除的标签')
    return
  }

  try {
    const result = await customerService.bulkAction({
      customer_ids: selectedCustomers.value,
      action: 'remove_tags',
      tag_ids: selectedTagIds
    })

    if (result.success) {
      toast.success(result.message || `成功为 ${selectedCustomers.value.length} 个客户移除标签`)
      // 清空选择
      selectedCustomers.value = []
      // 重新加载数据以更新显示
      await searchCustomers(false)
    } else {
      toast.error(result.message || '移除标签失败')
      // API调用失败时也清空选择
      selectedCustomers.value = []
    }
  } catch (error) {
    console.error('批量移除标签失败:', error)
    toast.error('移除标签失败，请稍后重试')
    // 出错时也要清空选择
    selectedCustomers.value = []
  }
}

const handleBulkChangeGroup = async (selectedGroupIds) => {
  if (selectedCustomers.value.length === 0) {
    toast.warning('请选择要修改分组的客户')
    return
  }

  try {
    const result = await customerService.bulkAction({
      customer_ids: selectedCustomers.value,
      action: 'update_group',
      group_ids: selectedGroupIds
    })

    if (result.success) {
      toast.success(result.message || `成功为 ${selectedCustomers.value.length} 个客户修改分组`)
      // 清空选择
      selectedCustomers.value = []
      // 重新加载数据以更新显示
      await searchCustomers(false)
    } else {
      toast.error(result.message || '修改分组失败')
      // API调用失败时也清空选择
      selectedCustomers.value = []
    }
  } catch (error) {
    console.error('批量修改分组失败:', error)
    toast.error('修改分组失败，请稍后重试')
    // 出错时也要清空选择
    selectedCustomers.value = []
  }
}

// 处理搜索
const handleSearch = () => {
  // 立即滚动到顶部
  window.scrollTo({
    top: 0,
    behavior: 'instant'
  })
  // 然后执行搜索
  searchCustomers(true)
}

// 重置所有筛选条件
const handleResetFilters = () => {
  searchQuery.value = ''
  selectedTag.value = ''
  selectedGroup.value = ''
  dateField.value = 'created_at'
  dateRange.value = { start: '', end: '' }
  handleSearch()
}

// 搜索客户
const searchCustomers = async (resetPage = true) => {
  if (resetPage) {
    currentPage.value = 1
    selectedCustomers.value = []
  }
  
  loading.value = true
  apiError.value = ''
  
  try {
    const params = {
      page: currentPage.value,
      page_size: pageSize.value
    }
    
    // 添加搜索条件
    if (searchQuery.value) {
      params.search = searchQuery.value
    }
    
    if (selectedTag.value) {
      params.tags = selectedTag.value
    }
    
    if (selectedGroup.value) {
      params.groups = selectedGroup.value
    }

    if (dateRange.value.start) {
      params.date_field = dateField.value
      params.date_start = dateRange.value.start
    }
    if (dateRange.value.end) {
      params.date_end = dateRange.value.end
    }

    const result = await customerService.getCustomers(params)
    
    if (result.success) {
      customers.value = result.data.results || []
      totalCustomers.value = result.data.count || 0
      
      // 如果没有任何筛选条件，将当前总数也作为全部客户数量
      if (!searchQuery.value && !selectedTag.value && !selectedGroup.value && !dateRange.value.start) {
        allCustomersCount.value = result.data.count || 0
      }
    } else {
      throw new Error(result.message)
    }
    
  } catch (error) {
    console.error('搜索客户失败:', error)
    apiError.value = error.message || '加载客户列表失败，请稍后重试'
    customers.value = []
    totalCustomers.value = 0
  } finally {
    loading.value = false
  }
}


// 分页方法
const prevPage = async () => {
  if (currentPage.value > 1) {
    window.scrollTo({
      top: 0,
      behavior: 'instant'
    })
    
    currentPage.value--
    selectedCustomers.value = []
    await searchCustomers(false)
  }
}

const nextPage = async () => {
  if (currentPage.value < totalPages.value) {
    window.scrollTo({
      top: 0,
      behavior: 'instant'
    })
    
    currentPage.value++
    selectedCustomers.value = []
    await searchCustomers(false)
  }
}

// 处理页码跳转
const handleJumpToPage = async () => {
  // 如果输入为空，直接返回
  if (!jumpToPage.value || jumpToPage.value === '') {
    return
  }
  
  const targetPage = parseInt(jumpToPage.value)
  
  // 验证页码范围
  if (!targetPage || targetPage < 1 || targetPage > totalPages.value) {
    toast.error(`请输入有效的页码（1-${totalPages.value}）`)
    jumpToPage.value = ''
    return
  }
  
  // 如果是当前页，不需要跳转
  if (targetPage === currentPage.value) {
    jumpToPage.value = ''
    return
  }
  
  // 滚动到顶部
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
  
  // 跳转到目标页
  currentPage.value = targetPage
  selectedCustomers.value = []
  jumpToPage.value = '' // 清空输入框
  await searchCustomers(false)
}

// 处理顶部页码跳转
const handleTopJumpToPage = async () => {
  if (!topJumpToPage.value || topJumpToPage.value === '') {
    return
  }
  
  const targetPage = parseInt(topJumpToPage.value)
  
  if (!targetPage || targetPage < 1 || targetPage > totalPages.value) {
    toast.error(`请输入有效的页码（1-${totalPages.value}）`)
    topJumpToPage.value = ''
    return
  }
  
  if (targetPage === currentPage.value) {
    topJumpToPage.value = ''
    return
  }
  
  currentPage.value = targetPage
  selectedCustomers.value = []
  topJumpToPage.value = ''
  await searchCustomers(false)
}

// 处理每页数量变化
const handlePageSizeChange = async () => {
  // 确保 pageSize 是数字类型
  pageSize.value = Number(pageSize.value)
  
  // 滚动到顶部
  window.scrollTo({
    top: 0,
    behavior: 'instant'
  })
  
  // 重置到第一页
  currentPage.value = 1
  selectedCustomers.value = []
  
  // 重新加载数据
  await searchCustomers(false)
}

// 格式化日期
const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).replace(/\//g, '-')
}

// 处理函数
const handleAddCustomer = () => {
  editingCustomer.value = null
  showCustomerModal.value = true
}

const handleSendEmail = (customer) => {
  // 存储目标客户信息，用于预填收件人
  targetCustomer.value = customer
  bulkEmailRecipients.value = ''
  showEmailModal.value = true
}

const handleBulkSendEmail = () => {
  if (selectedCustomers.value.length === 0) {
    toast.warning('请先选择要群发邮件的客户')
    return
  }
  
  // 🔧 获取选中客户的完整信息（包括ID），而不仅仅是邮箱地址
  const selectedCustomerObjects = customers.value
    .filter(customer => selectedCustomers.value.includes(customer.id))
    .filter(customer => customer.email) // 过滤掉空邮箱
    .map(customer => ({
      id: customer.id,           // 🔧 客户ID - 用于后端查找真实邮箱
      email: customer.email,
      name: customer.first_name && customer.last_name 
        ? `${customer.first_name} ${customer.last_name}` 
        : (customer.first_name || customer.last_name || customer.email)
    }))
  
  if (selectedCustomerObjects.length === 0) {
    toast.warning('选中的客户中没有有效的邮箱地址')
    return
  }
  
  // 🔧 传递客户对象数组（JSON字符串格式），而不是简单的邮箱字符串
  bulkEmailRecipients.value = JSON.stringify(selectedCustomerObjects)
  targetCustomer.value = null // 群发模式不使用单个客户
  showEmailModal.value = true
}

const handleEditCustomer = (customer) => {
  editingCustomer.value = customer
  showCustomerModal.value = true
}

const closeCustomerModal = () => {
  showCustomerModal.value = false
}

const closeEmailModal = () => {
  showEmailModal.value = false
  targetCustomer.value = null
  bulkEmailRecipients.value = ''
}

const onEmailSent = () => {
  // 邮件发送成功后的处理
  console.log('邮件发送成功')
  
  // 如果是群发邮件，清空选中的客户
  if (bulkEmailRecipients.value) {
    selectedCustomers.value = []
  }
}

const handleCustomerModalSuccess = (updatedCustomer) => {
  if (updatedCustomer && editingCustomer.value) {
    // 编辑模式：直接更新现有客户数据
    const customerIndex = customers.value.findIndex(c => c.id === updatedCustomer.id)
    if (customerIndex !== -1) {
      // 保持响应式更新
      customers.value[customerIndex] = { ...updatedCustomer }
      
      // 触发表格行高度同步
      nextTick(() => {
        syncTableRowHeights()
      })
      
      return
    }
  }
  
  // 新建模式或找不到客户时，重新加载列表
  searchCustomers(false)
  loadAllCustomersCount()
}

const handleDeleteCustomer = async (customer) => {
  if (confirm(`⚠️ 确定要永久删除客户 ${customer.first_name} ${customer.last_name} 吗？\n\n删除后将从数据库中彻底移除，无法恢复！\n\n相关的标签、分组关联关系也将被清除。`)) {
    customer.deleting = true
    
    try {
      // 调用真实的删除API
      const result = await customerService.deleteCustomer(customer.id)
      
      if (result.success) {
        toast.success(result.data?.message || `客户 ${customer.first_name} ${customer.last_name} 已删除`)
        
        // 刷新数据以确保前后端一致性
        await searchCustomers(false)
        await loadAllCustomersCount()
        
        // 如果当前页没有数据了，且不是第一页，则跳转到上一页
        if (customers.value.length === 0 && currentPage.value > 1) {
          currentPage.value--
          await searchCustomers(false)
        }
      } else {
        toast.error(result.message || '删除客户失败')
      }
    } catch (error) {
      console.error('删除客户失败:', error)
      
      if (error.response?.status === 403) {
        toast.error('没有权限删除此客户')
      } else if (error.response?.status === 404) {
        toast.error('客户不存在或已被删除')
        await searchCustomers(false)
      } else {
        const errorMsg = error.response?.data?.message || error.message || '删除失败'
        toast.error(`删除客户失败: ${errorMsg}`)
      }
    } finally {
      customer.deleting = false
    }
  }
}

const handleManageTags = () => {
  showTagManagementModal.value = true
}

// 关闭标签管理模态框
const closeTagManagementModal = () => {
  showTagManagementModal.value = false
}

// 标签管理成功回调
const handleTagManagementSuccess = async () => {
  // 重新加载标签和分组数据
  await loadTagsAndGroups()
  // 重新加载客户列表以更新标签显示
  await searchCustomers(false)
}

const handleManageGroups = () => {
  showGroupManagementModal.value = true
}

// 自定义字段管理
const handleManageCustomFields = () => {
  showCustomFieldManagementModal.value = true
}

const closeCustomFieldManagementModal = () => {
  showCustomFieldManagementModal.value = false
}

const handleCustomFieldManagementSuccess = async () => {
  // 重新加载字段配置以反映新增的自定义字段
  await loadFieldConfigs()
  // 触发客户模态框中的自定义字段重新加载
  customFieldsUpdateTrigger.value++
}

// 字段配置管理
const handleFieldConfig = () => {
  showFieldConfigModal.value = true
}

const closeFieldConfigModal = () => {
  showFieldConfigModal.value = false
}

const handleFieldConfigSuccess = async () => {
  // 字段配置更新后，重新加载字段配置和客户列表
  await loadFieldConfigs()
  await searchCustomers(false)
}

// 关闭分组管理模态框
const closeGroupManagementModal = () => {
  showGroupManagementModal.value = false
}

// 分组管理成功回调
const handleGroupManagementSuccess = async () => {
  // 重新加载分组数据
  await loadTagsAndGroups()
}

// 导出相关方法
const closeExportModal = () => {
  showExportModal.value = false
}

const handleConfirmExport = async (exportConfig) => {
  try {
    let exportData = {
      format: exportConfig.format
    }
    
    // 根据导出范围设置参数
    if (exportConfig.range === 'selected') {
      exportData.customer_ids = selectedCustomers.value
    } else if (exportConfig.range === 'filtered') {
      // 添加当前的筛选条件
      if (searchQuery.value.trim()) {
        exportData.search = searchQuery.value.trim()
      }
      if (selectedTag.value) {
        exportData.tag_ids = [selectedTag.value]
      }
      if (selectedGroup.value) {
        exportData.group_ids = [selectedGroup.value]
      }
    }
    // 如果是 'all'，不需要添加额外参数
    
    // 调用导出API
    const result = await customerService.exportCustomers(exportData)
    
    if (result.success) {
      // 创建下载链接
      let blob
      if (result.filename.toLowerCase().endsWith('.csv')) {
        // 对CSV文件特别处理，确保正确的编码和MIME类型
        blob = new Blob([result.data], { 
          type: 'text/csv;charset=utf-8' 
        })
      } else if (result.filename.toLowerCase().endsWith('.xlsx')) {
        // 对Excel文件特别处理，确保正确的MIME类型
        blob = new Blob([result.data], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        })
      } else {
        blob = new Blob([result.data], { type: result.data.type })
      }
      
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = result.filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      toast.success('客户数据导出成功')
    } else {
      throw new Error(result.message || '导出失败')
    }
  } catch (error) {
    console.error('导出客户失败:', error)
    toast.error(error.message || '导出客户失败，请稍后重试')
  } finally {
    exportConfig.callback()
  }
}

  const handleImport = () => {
    showImportModal.value = true
  }
  
  const closeImportModal = () => {
    showImportModal.value = false
  }
  
  const handleImportCompleted = (result) => {
    console.log('导入完成:', result)
    // 重新加载客户列表和全部客户数量
    searchCustomers()
    loadAllCustomersCount()
    
    // 显示导入结果
    if (result.data && result.data.summary) {
      const summary = result.data.summary
      toast.success(`导入完成：成功 ${summary.success_count} 个，跳过 ${summary.skip_count || summary.duplicate_count || 0} 个，失败 ${summary.error_count || 0} 个`)
    }
  }

const handleExport = async () => {
  console.log('导出客户')
  // 确保有准确的全部客户数量
  await loadAllCustomersCount()
  showExportModal.value = true
}

// 处理标签点击选中
const handleTagClick = (customerId, tagId, event) => {
  event.stopPropagation() // 阻止事件冒泡
  const tagKey = `${customerId}-${tagId}`
  if (selectedTagForRemoval.value === tagKey) {
    // 如果已经选中，则取消选中
    selectedTagForRemoval.value = null
  } else {
    // 选中该标签
    selectedTagForRemoval.value = tagKey
  }
}

// 从客户移除标签
const handleRemoveTagFromCustomer = async (customer, tag) => {
  if (!confirm(`确定要从"${customer.first_name} ${customer.last_name}"移除标签"${tag.name}"吗？`)) {
    return
  }

  // 设置加载状态
  customer.removingTag = tag.id
  
  try {
    const result = await customerService.removeTagFromCustomer(customer.id, tag.id)
    
    if (result.success) {
      // 从前端客户数据中移除该标签
      const tagIndex = customer.tags.findIndex(t => t.id === tag.id)
      if (tagIndex !== -1) {
        customer.tags.splice(tagIndex, 1)
      }
      
      toast.success('标签移除成功')
    } else {
      toast.error(result.message || '移除标签失败')
    }
  } catch (error) {
    console.error('移除标签失败:', error)
    toast.error('移除标签失败')
  } finally {
    // 清除加载状态
    delete customer.removingTag
    // 清除选中状态
    selectedTagForRemoval.value = null
  }
}


// 渲染字段值
const renderFieldValue = (customer, fieldConfig) => {
  const fieldName = fieldConfig.field_name
  
  // 处理自定义字段
  if (fieldConfig.field_type === 'custom') {
    const customFieldValue = customer.custom_fields?.find(
      value => value.field_id === parseInt(fieldName)
    )
    return customFieldValue?.value || '-'
  }
  
  // 处理系统字段
  switch (fieldName) {
    case 'first_name':
      // 只显示姓名
      return `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || '未命名'
    case 'email':
      // 只显示邮箱
      return customer.email || '-'
    case 'owned_email_account':
      // 归属邮箱
      return customer.owned_email_account_email || '-'
    case 'phone':
      return customer.phone || '-'
    case 'order_number':
      return customer.order_number || '-'
    case 'source':
      return customer.source || '-'
    case 'address':
      return {
        type: 'address',
        value: customer.address || '-'
      }
    case 'tags':
      return {
        type: 'tags',
        value: customer.tags || []
      }
    case 'groups':
      return {
        type: 'groups',
        value: customer.groups || []
      }
    case 'remark':
      return {
        type: 'remark',
        value: customer.notes || '-'
      }
    case 'created_at':
      return formatDate(customer.created_at)
    case 'actions':
      return {
        type: 'actions',
        customer: customer
      }
    default:
      return customer[fieldName] || '-'
  }
}

// 加载字段配置
const loadFieldConfigs = async () => {
  try {
    const response = await customerFieldConfigService.getVisibleFieldConfigs()
    if (response.success) {
      fieldConfigs.value = response.data || []
    }
  } catch (error) {
    console.error('加载字段配置失败:', error)
    // 如果加载失败，初始化默认配置
    try {
      await customerFieldConfigService.initializeFieldConfigs()
      const retryResponse = await customerFieldConfigService.getVisibleFieldConfigs()
      if (retryResponse.success) {
        fieldConfigs.value = retryResponse.data || []
      }
    } catch (initError) {
      console.error('初始化字段配置失败:', initError)
    }
  }
}

// 加载标签和分组数据
const loadTagsAndGroups = async () => {
  try {
    // 加载标签
    const tagsResult = await customerTagService.getTags()
    if (tagsResult.success) {
      availableTags.value = tagsResult.data.results || []
    }
    
    // 加载分组
    const groupsResult = await customerGroupService.getGroups()
    if (groupsResult.success) {
      availableGroups.value = groupsResult.data.results || []
    }
  } catch (error) {
    console.error('加载标签和分组失败:', error)
  }
}

// 加载全部客户数量（不受筛选影响）- 仅在必要时调用
const loadAllCustomersCount = async () => {
  // 如果当前没有筛选条件，说明 allCustomersCount 已经是准确的
  if (!searchQuery.value && !selectedTag.value && !selectedGroup.value) {
    return
  }
  
  try {
    const result = await customerService.getCustomers({
      page: 1,
      page_size: 1 // 只需要获取总数，不需要具体数据
    })
    if (result.success && result.data.count !== undefined) {
      allCustomersCount.value = result.data.count
    }
  } catch (error) {
    console.error('获取全部客户数量失败:', error)
  }
}

// 监听客户数据变化，同步表格行高度
watch(customers, () => {
  syncTableRowHeights()
}, { deep: true })

// 监听标签删除操作后的变化
watch(selectedTagForRemoval, () => {
  // 当标签选择状态改变时，重新同步高度
  nextTick(() => {
    syncTableRowHeights()
  })
})

// 窗口大小调整处理函数
const handleResize = () => {
  syncTableRowHeights()
}

// 组件挂载时加载数据
onMounted(async () => {
  await Promise.all([
    loadTagsAndGroups(),
    loadFieldConfigs() // 加载字段配置
  ])
  await searchCustomers() // 这里会同时设置 totalCustomers 和 allCustomersCount（如果无筛选条件）
  // 确保初始数据加载后同步高度
  await nextTick()
  syncTableRowHeights()
  
  // 添加窗口调整事件监听器
  window.addEventListener('resize', handleResize)
})

// 组件卸载时清理事件监听器
onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})
</script>

<style scoped>
/* 移除全局滚动条设置，让布局容器控制滚动行为 */

/* 美化表格容器的水平滚动条 */
.table-container::-webkit-scrollbar {
  height: 8px;
}

.table-container::-webkit-scrollbar-track {
  background: #f8fafc;
  border-radius: 4px;
  margin: 0 4px;
}

.table-container::-webkit-scrollbar-thumb {
  background: linear-gradient(90deg, #e2e8f0, #cbd5e1);
  border-radius: 4px;
  transition: all 0.3s ease;
}

.table-container::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(90deg, #cbd5e1, #94a3b8);
  transform: scaleY(1.2);
}

/* 移除不需要的自定义滚动条样式，让布局容器处理滚动 */

/* Firefox 滚动条样式 */
.table-container {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f8fafc;
}

/* 保留下拉选择器和模态框的滚动条美化 */
.dropdown-content::-webkit-scrollbar,
.modal-content::-webkit-scrollbar,
.select-options::-webkit-scrollbar {
  width: 4px;
  height: 4px;
}

.dropdown-content::-webkit-scrollbar-track,
.modal-content::-webkit-scrollbar-track,
.select-options::-webkit-scrollbar-track {
  background: #f8fafc;
  border-radius: 2px;
}

.dropdown-content::-webkit-scrollbar-thumb,
.modal-content::-webkit-scrollbar-thumb,
.select-options::-webkit-scrollbar-thumb {
  background: #e2e8f0;
  border-radius: 2px;
  transition: background 0.2s ease;
}

.dropdown-content::-webkit-scrollbar-thumb:hover,
.modal-content::-webkit-scrollbar-thumb:hover,
.select-options::-webkit-scrollbar-thumb:hover {
  background: #cbd5e1;
}
</style>
